import React from 'react';

import { useConfiguration } from '../../context/configurationContext';

import { ensureListing } from '../../util/data';
import { FormattedMessage, useIntl } from '../../util/reactIntl';

import { IconSpinner, NamedLink } from '../../components';
import { useViewportSize } from '../../hooks';

import GradingInfo from '../GradingInfo/GradingInfo';
import IconCard from '../SavedCardDetails/IconCard/IconCard';
import ListingCarousel from '../../containers/PageBuilder/SectionBuilder/SectionCarousel/ListingCarousel/ListingCarousel';

import css from './CollectiblesAndAccessories.module.css';

const SEARCH_QUERY = '?pub_categoryLevel1=collectibles-and-accessories';

const humanizeValue = value =>
  String(value || '')
    .replace(/[-_]+/g, ' ')
    .replace(/\b\w/g, character => character.toUpperCase());

const getFieldLabel = (intl, listingFields, key, value, fallbackValue) => {
  if (!value) return '';

  const fieldConfig = listingFields?.find(field => field.key === key);
  const optionConfig = fieldConfig?.enumOptions?.find(option => `${option.option}` === `${value}`);
  const defaultMessage = fallbackValue || optionConfig?.label || humanizeValue(value);

  return intl.formatMessage(
    { id: `ListingPage.field.${key}.option.${value}`, defaultMessage },
    {}
  );
};

const getConditionClassName = conditionValue => {
  const normalizedValue = String(conditionValue || '')
    .toLowerCase()
    .replace(/[_\s]+/g, '-');

  if (normalizedValue.includes('excellent')) return css.conditionExcellent;
  if (normalizedValue.includes('very-good')) return css.conditionVeryGood;
  if (normalizedValue.includes('near-mint')) return css.conditionNearMint;

  return css.conditionGood;
};

/**
 * Landing-page carousel for collectibles and accessories listings.
 *
 * @component
 * @param {Object} props
 * @param {Array} props.listings category-filtered listing entities
 * @param {boolean} props.inProgress whether listings are being loaded
 * @param {Object?} props.error listing query error
 * @returns {JSX.Element}
 */
const CollectiblesAndAccessories = props => {
  const { listings = [], inProgress, error } = props;
  const intl = useIntl();
  const config = useConfiguration();
  const listingFields = config?.listing?.listingFields || [];
  const viewportSize = useViewportSize();
  const showDots = viewportSize.width < 600;

  return (
    <div className={css.root}>
      <div className={css.header}>
        <div className={css.headerCopy}>
          <h2 className={css.title}>
            <FormattedMessage id="CollectiblesAndAccessories.title" />
          </h2>
          <p className={css.description}>
            <FormattedMessage id="CollectiblesAndAccessories.description" />
          </p>
        </div>

        <NamedLink name="SearchPage" to={{ search: SEARCH_QUERY }} className={css.viewAllLink}>
          <FormattedMessage id="CollectiblesAndAccessories.viewAll" />
          <IconCard brand="rightArrow" />
        </NamedLink>
      </div>

      {inProgress ? (
        <div className={css.loadingState}>
          <IconSpinner rootClassName={css.spinner} />
        </div>
      ) : error ? (
        <div className={css.emptyState} role="status">
          <FormattedMessage id="CollectiblesAndAccessories.error" />
        </div>
      ) : listings.length === 0 ? (
        <div className={css.emptyState} role="status">
          <FormattedMessage id="CollectiblesAndAccessories.empty" />
        </div>
      ) : (
        <div className={css.collectionCarouselWrapper}>
          <ListingCarousel
            id="collectibles-carousel"
            listings={listings}
            slidesToShow={4}
            mobileSlidesToShow={1.25}
            showArrows={listings.length > 4}
            showDots={showDots}
            autoplay={false}
            emblaSettings={{ loop: false, align: 'start' }}
            listingCardClassName={css.collectiblesListingCard}
            renderListingDetails={listing => {
              const currentListing = ensureListing(listing);
              const { publicData = {} } = currentListing.attributes;
              const dimensionsValue = publicData.dimensions;
              const isCustomDimension = dimensionsValue === 'custom-size';
              const customTitle = publicData.custom_dimensions || null;
              const customDescription = publicData.custom_dimensions_description || null;
              const dimensionsLabel = isCustomDimension
                ? customTitle
                : getFieldLabel(intl, listingFields, 'dimensions', dimensionsValue);
              const conditionLabel = getFieldLabel(
                intl,
                listingFields,
                'poster_condition',
                publicData.poster_condition
              );
              if (!dimensionsLabel && !conditionLabel) return null;

              return (
                <div className={css.listingDetails}>
                  <div className={css.badges}>
                    {dimensionsLabel ? (
                      <span className={css.dimensionBadge}>
                        {dimensionsLabel}
                        {isCustomDimension && customDescription ? (
                          <span
                            className={css.customInfoIcon}
                            aria-label={customDescription}
                            data-tooltip={customDescription}
                          >
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 14 14"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                              aria-hidden="true"
                            >
                              <path
                                d="M7 12.6453A5.6452 5.6452 0 1 0 7 1.35498a5.6452 5.6452 0 0 0 0 11.29032Z"
                                stroke="currentColor"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M7 9.33301V6.99967M7 4.6665h.00645"
                                stroke="currentColor"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </span>
                        ) : null}
                      </span>
                    ) : null}
                    {conditionLabel ? (
                      <span
                        className={`${css.conditionBadge} ${getConditionClassName(
                          publicData.poster_condition
                        )}`}
                      >
                        {conditionLabel}
                      </span>
                    ) : null}
                  </div>
                </div>
              );
            }}
          />
        </div>
      )}

      <div className={css.sizeGuideBanner}>
        <span className={css.infoIcon} aria-hidden="true">
          i
        </span>
        <span className={css.sizeGuideCopy}>
          <FormattedMessage id="CollectiblesAndAccessories.sizeGuideDescription" />
          {' '}
          <GradingInfo
            type="dimensions"
            hideCustomGenericOption
            triggerClassName={css.sizeGuideButton}
            triggerContent={
              <>
                <FormattedMessage id="CollectiblesAndAccessories.sizeGuide" />
                <IconCard brand="rightArrow" />
              </>
            }
          />
        </span>
      </div>
    </div>
  );
};

export default CollectiblesAndAccessories;
